import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonBase from "@mui/material/ButtonBase";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ConfirmDialog } from "../../../../../components/primitives/feedback/ConfirmDialog";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import { usePopupMenu } from "../../../../../components/primitives/navigation/PopupMenu";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { DocumentLink } from "../../../../../components/domain/data-display/DocumentLink";
import { CarriedForwardBadge } from "../../../../../components/domain/data-display/CarriedForwardBadge";
import { FieldReannotationOutcomeBadge } from "../../../../../components/domain/data-display/FieldReannotationOutcomeBadge";
import { DocumentDetailDialog } from "../../../Library/Documents/DocumentDetailDialog";
import { FieldReannotationJustificationsDialog } from "./FieldReannotationJustificationsDialog";
import { fieldReannotationFieldLabel } from "./field_reannotation_fields";
import { FIELD_REANNOTATION_TAB_ORDER, FIELD_REANNOTATION_TABS } from "./field_reannotation_tabs";
import type {
  FieldReannotationRow,
  UseFieldReannotationPanelResult,
} from "./useFieldReannotationPanel";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";

function labelsText(labels: string[]): string {
  return labels.length > 0 ? labels.join(", ") : "none";
}

/** What the re-annotation found on one document, in labels: the AI's before and after, and — for a
 *  contradiction — the person's label it now disagrees with. */
function RowLabels({ row }: { row: FieldReannotationRow }) {
  const { result, item } = row;
  if (!result) {
    return item.error ? (
      <Typography variant="caption" color="error">
        {item.error}
      </Typography>
    ) : (
      <>—</>
    );
  }
  if (result.outcome === "skipped") {
    return (
      <Typography variant="caption" color="text.secondary">
        {result.reason ?? "Nothing to re-annotate."}
      </Typography>
    );
  }
  return (
    <Stack gap={0.5} sx={{ alignItems: "flex-start" }}>
      {result.human && (
        <Typography variant="body2">
          Person: {labelsText(result.human.labels)}{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            ({result.human.state})
          </Typography>
        </Typography>
      )}
      {result.change ? (
        <Typography variant="body2">
          AI: {labelsText(result.change.before)} → {labelsText(result.change.after)}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          AI labels unchanged
        </Typography>
      )}
      {result.carriedForwardFromItemId && <CarriedForwardBadge />}
    </Stack>
  );
}

interface RowActionsProps {
  reviewable: boolean;
  loading: boolean;
  error: string | null;
  onOverride: () => void;
  onKeep: () => void;
  onOpenDetail: () => void;
  /** Absent when the row has no AI change to justify. */
  onOpenJustifications?: () => void;
}

function RowActions({
  reviewable,
  loading,
  error,
  onOverride,
  onKeep,
  onOpenDetail,
  onOpenJustifications,
}: RowActionsProps) {
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [
      { label: "Details", onClick: onOpenDetail },
      ...(onOpenJustifications
        ? [{ label: "AI justification", onClick: onOpenJustifications }]
        : []),
    ],
    ...(reviewable ? [[{ label: "Keep person's label", onClick: onKeep }]] : []),
    ...(reviewable
      ? [[{ label: "Override with AI's label", onClick: onOverride, color: "error" as const }]]
      : []),
  ]);

  if (loading) return <CircularProgress size={18} />;
  return (
    <Stack direction="row" gap={0.5} sx={{ alignItems: "center" }}>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <IconButton size="small" onClick={setPopupMenuAnchor} aria-label="Row actions">
        <MoreVertIcon />
      </IconButton>
      {menu}
    </Stack>
  );
}

interface LabelFilterProps {
  id: string;
  label: string;
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
}

function LabelFilter({ id, label, options, value, onChange }: LabelFilterProps) {
  return (
    <FormControl size="small" sx={{ minWidth: 240 }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <MenuItem value="">Any</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export interface FieldReannotationPanelBodyProps {
  panel: UseFieldReannotationPanelResult;
}

/** Presentational field_reannotation results panel: one tab per outcome, each a paginated list of
 *  the documents that landed there. On the contradictions tab a document's row can be settled —
 *  override the person's label with the AI's, or keep it. A row opens its document details. */
export function FieldReannotationPanelBody({ panel }: FieldReannotationPanelBodyProps) {
  const columns: DataSequenceColumn<FieldReannotationRow>[] = [
    {
      key: "subject",
      header: "Document",
      width: 260,
      cardRole: "primary",
      cell: (row) => <DocumentLink title={row.item.subjectTitle} url={row.item.subjectUrl} />,
    },
    {
      key: "outcome",
      header: "Outcome",
      cell: (row) =>
        row.result ? (
          <FieldReannotationOutcomeBadge outcome={row.result.outcome} review={row.item.review} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.item.status === "failed" ? "Failed" : "…"}
          </Typography>
        ),
    },
    {
      key: "labels",
      header: "Labels",
      cell: (row) =>
        row.result?.change ? (
          <ButtonBase
            onClick={() => panel.justifications.open(row)}
            aria-label="Show the AI's justification"
            sx={{ textAlign: "left", display: "block", cursor: "pointer" }}
          >
            <RowLabels row={row} />
          </ButtonBase>
        ) : (
          <RowLabels row={row} />
        ),
    },
    {
      key: "actions",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <RowActions
          reviewable={panel.tabs.tab.reviewable && row.item.review === "open"}
          loading={panel.review.keep.keepingId === row.item.id && panel.review.keep.loading}
          error={panel.review.keep.keepingId === row.item.id ? panel.review.keep.error : null}
          onOverride={() => panel.review.apply.open(row)}
          onKeep={() => panel.review.keep.handle(row)}
          onOpenDetail={() => panel.detail.open(row)}
          onOpenJustifications={
            row.result?.change ? () => panel.justifications.open(row) : undefined
          }
        />
      ),
    },
  ];

  return (
    <Stack gap={2}>
      <Typography variant="caption" color="text.secondary">
        Re-annotating {fieldReannotationFieldLabel(panel.params.field)}
      </Typography>

      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {FIELD_REANNOTATION_TAB_ORDER.map((key) => (
          <Tab key={key} label={FIELD_REANNOTATION_TABS[key].label} value={key} />
        ))}
      </Tabs>

      <Stack direction="row" gap={1.5} sx={{ flexWrap: "wrap" }}>
        <LabelFilter
          id="old-label-filter"
          label="Old label"
          options={panel.filters.labelOptions}
          value={panel.filters.oldLabel}
          onChange={panel.filters.setOldLabel}
        />
        <LabelFilter
          id="new-label-filter"
          label="New label"
          options={panel.filters.labelOptions}
          value={panel.filters.newLabel}
          onChange={panel.filters.setNewLabel}
        />
      </Stack>

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}

      {panel.tabs.tab.reviewable && panel.selection.count > 0 && (
        <Stack
          direction="row"
          gap={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="body2">{panel.selection.count} selected</Typography>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" size="small" onClick={panel.review.bulkKeep.open}>
              Keep person's label ({panel.selection.count})
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={panel.review.bulkApply.open}
            >
              Override with AI's label ({panel.selection.count})
            </Button>
          </Stack>
        </Stack>
      )}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          selection={panel.selection.table}
          emptyMessage="No documents in this list."
          pagination={panel.list.pagination}
        />
      )}

      <ConfirmDialog {...panel.review.apply.dialogProps} />

      <ConfirmDialog
        {...panel.review.bulkKeep.dialogProps}
        progress={panel.review.bulkKeep.progress}
      />

      <ConfirmDialog
        {...panel.review.bulkApply.dialogProps}
        progress={panel.review.bulkApply.progress}
      />

      <FieldReannotationJustificationsDialog
        row={panel.justifications.target}
        onClose={panel.justifications.close}
      />

      <DocumentDetailDialog item={panel.detail.target} onClose={panel.detail.close} />
    </Stack>
  );
}
